import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { useLang } from "../../lib/i18n";

// Full-screen, zoomable product gallery rendered into a portal.
export const ProductLightbox = ({ products, index, onClose, onNavigate }) => {
  const { t } = useLang();
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });

  const open = index !== null && index >= 0;
  const product = open ? products[index] : null;

  // reset zoom whenever the active image changes
  useEffect(() => setZoom(false), [index]);

  const go = useCallback(
    (dir) => {
      if (!open) return;
      const next = (index + dir + products.length) % products.length;
      onNavigate(next);
    },
    [open, index, products.length, onNavigate]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, go]);

  const handleImgClick = (e) => {
    if (!zoom) {
      const rect = e.currentTarget.getBoundingClientRect();
      setOrigin({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100,
      });
    }
    setZoom((z) => !z);
  };

  const handleMove = (e) => {
    if (!zoom) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setOrigin({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[120] bg-black/95 backdrop-blur-xl flex flex-col"
          data-testid="product-lightbox"
        >
          {/* top bar */}
          <div className="flex items-center justify-between px-5 md:px-10 py-5 shrink-0">
            <div>
              <p className="font-display text-lg md:text-2xl font-bold text-white">Gesung {product.model}</p>
              <p className="text-[11px] font-mono text-lime2">{product.kw} kW</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setZoom((z) => !z)}
                className="rounded-full border border-white/15 p-2.5 text-white/70 hover:text-lime2 hover:border-lime2/50 transition-colors"
                data-testid="lightbox-zoom-toggle"
                aria-label="Toggle zoom"
              >
                {zoom ? <ZoomOut size={18} /> : <ZoomIn size={18} />}
              </button>
              <button
                onClick={onClose}
                className="rounded-full border border-white/15 p-2.5 text-white/70 hover:text-white hover:border-white/40 transition-colors"
                data-testid="lightbox-close"
                aria-label={t.catalog.galleryClose}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* image stage */}
          <div className="relative flex-1 flex items-center justify-center px-4 md:px-16 overflow-hidden">
            <button
              onClick={() => go(-1)}
              className="absolute left-3 md:left-8 z-10 rounded-full border border-white/15 bg-black/40 p-3 text-white/70 hover:text-lime2 hover:border-lime2/50 transition-colors"
              data-testid="lightbox-prev"
              aria-label={t.catalog.galleryPrev}
            >
              <ChevronLeft size={22} />
            </button>

            <div className="relative max-w-5xl w-full aspect-[16/9] overflow-hidden rounded-xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key={product.img}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full h-full"
                >
                  <img
                    src={product.img}
                    alt={`Gesung ${product.model}`}
                    onClick={handleImgClick}
                    onMouseMove={handleMove}
                    style={{
                      transform: zoom ? "scale(2.2)" : "scale(1)",
                      transformOrigin: `${origin.x}% ${origin.y}%`,
                      cursor: zoom ? "zoom-out" : "zoom-in",
                    }}
                    className="w-full h-full object-cover transition-transform duration-300"
                    data-testid="lightbox-image"
                    draggable={false}
                  />
                </motion.div>
              </AnimatePresence>
            </div>

            <button
              onClick={() => go(1)}
              className="absolute right-3 md:right-8 z-10 rounded-full border border-white/15 bg-black/40 p-3 text-white/70 hover:text-lime2 hover:border-lime2/50 transition-colors"
              data-testid="lightbox-next"
              aria-label={t.catalog.galleryNext}
            >
              <ChevronRight size={22} />
            </button>
          </div>

          {/* thumbnails */}
          <div className="flex items-center justify-center gap-3 px-5 py-6 shrink-0">
            {products.map((p, i) => (
              <button
                key={p.model}
                onClick={() => onNavigate(i)}
                data-testid={`lightbox-thumb-${p.model}`}
                className={`relative h-14 w-20 md:h-16 md:w-24 overflow-hidden rounded-md border transition-all duration-300 ${
                  i === index ? "border-lime2 opacity-100" : "border-white/10 opacity-50 hover:opacity-90"
                }`}
              >
                <img src={p.img} alt={p.model} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
